import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Tippy from '@tippyjs/react';
import { useRecoilState } from 'recoil';

import { useUIContext } from 'lib/context/UIContext/UIContext';
import useLoading from 'lib/hooks/useLoading';

import { useClinicalNoteContext } from '../../../../../../lib/context/ClinicalNoteContext/ClinicalNoteContext';
import Button from '../../../../../shared/Buttons/Button';
import { clinicalNote as clinicalNoteState } from '../../../../../state';
import { showPreviewNote } from '../../lib/showPreviewNote';
import ConfirmSignLock from '../../shared/ConfirmSignLock';

import { onShowConfirmSignLock as handleShowConfirmSignLock } from './lib/helpers';
import useClinicalNoteSignatures from './lib/hooks/useClinicalNoteSignatures';

const SignAndLock = ({ buttonProps }) => {
  const {
    setAdvancedHP,
    setAdvancedSOAP,
    setCustomFormTypes,
    showConfirmSignLock,
    saveButtonText,
    documentSignInfo,
    setIsExport,
    setShowPreviewNoteLoading,
    navigate,
    saveNoteRef = null,
    setDocumentSignInfo
  } = useClinicalNoteContext();
  const [clinicalNote, setClinicalNote] = useRecoilState(clinicalNoteState);
  const { signLockButtonInfo = null } = documentSignInfo || {};
  const { id, appointmentId } = useParams() || {};
  const { loading, startLoading, stopLoading } = useLoading();
  const { device } = useUIContext();
  const [showSignAndLock, setShowSignAndLock] = useState(window?.innerWidth >= 1220);
  const { userSign, documentSignatures, userAssignedToDocument } = useClinicalNoteSignatures({
    setDocumentSignInfo,
    saveButtonText
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1220) {
        setShowSignAndLock(true);
      } else {
        setShowSignAndLock(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onShowConfirmSignLock = async () => {
    await handleShowConfirmSignLock({
      clinicalNote,
      showConfirmSignLock,
      params: { id, appointmentId },
      stopLoading,
      startLoading,
      saveButtonText,
      setClinicalNote,
      setAdvancedHP,
      setAdvancedSOAP,
      setCustomFormTypes,
      setIsExport,
      saveNoteRef,
      navigate,
      setShowPreviewNoteLoading,
      showPreviewNote
    });
  };

  if (!showSignAndLock) {
    return null;
  }

  return (
    <div className="flex items-center gap-x-2">
      <Tippy
        placement="bottom"
        className="tippy-dark"
        content={signLockButtonInfo}
        disabled={!signLockButtonInfo}>
        <div>
          <Button
            icon={userSign ? 'lock-2-opened-mono' : 'lock-2-mono'}
            data-qa="sign-lock-unlock-btn"
            {...(['desktop', 'laptop', 'tablet-lg'].includes(device) && {
              text: userSign ? 'Unlock' : 'Sign & Lock'
            })}
            className="hover:!bg-primary-700"
            textClassName="text-sm text-white font-500"
            shade={100}
            transparent
            size="small"
            {...buttonProps}
            disabled={
              saveButtonText === 'Saving' ||
              !clinicalNote?.id ||
              !userAssignedToDocument?.id ||
              loading ||
              buttonProps?.disabled
            }
            onClick={(...r) => {
              if (buttonProps?.onClick) {
                buttonProps?.onClick();
                setTimeout(() => onShowConfirmSignLock(...r), 100);
              } else {
                onShowConfirmSignLock(...r);
              }
            }}
          />
        </div>
      </Tippy>

      <ConfirmSignLock documentSignatures={documentSignatures} hasUserSigned={userSign} />
    </div>
  );
};

export default SignAndLock;
